/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import { Row, Col, Hidden } from "react-awesome-styled-grid";
import { When } from "react-if";
import { Link } from "react-router-dom";
import {
  Box,
  FlexContainer,
  Tooltip,
  SVGIcon,
  PieChart,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  useGetDefaultApplicationQuery,
  GetDefaultApplicationQuery,
} from "~/generated/graphql";
import { Co2eSavings, WasteIndicator, LoaderWrap } from "~/components";
import { getApplicationStatus } from "~/components/ApplicationItem";
import { useCurrency } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { formatMoneyWithFallback } from "~/tools";

type SearchResult = NonNullable<
  NonNullable<GetDefaultApplicationQuery["defaultApplication"]>
>;

type Application = Extract<SearchResult, { __typename?: "Application" }>;

type Environment = NonNullable<Application["environments"]>[0];

export function DefaultApplication() {
  const [application, setApplication] = React.useState<
    Application | undefined
  >();
  const [resources, setResources] = React.useState<number>(0);

  const { data: defaultAppData, loading } = useGetDefaultApplicationQuery();

  const { currency } = useCurrency();

  React.useEffect(() => {
    const defaultApp = defaultAppData?.defaultApplication;

    if (defaultApp?.__typename === "Application") {
      setApplication(defaultApp);

      const environments = application?.environments ?? [];
      setResources(
        environments.reduce(
          (sum: number, env: Environment) => sum + (env?.resourceCount ?? 0),
          0
        )
      );
    }
  }, [defaultAppData, application]);

  const forecastToPrevious =
    (100 * Number(application?.cost?.forecast)) /
    Number(application?.cost?.previousMonth);
  const status = getApplicationStatus(forecastToPrevious);

  const showCo2eSavings = parseFloat(application?.co2e ?? "0") > 0;

  return (
    <Box spacing="spacing04">
      <Box boxStyle="lightGrey">
        <LoaderWrap inContent loading={loading}>
          <Row>
            <Col sm={5} md={4} lg={7}>
              <FlexContainer>
                <Hidden xs>
                  <Tooltip
                    status={status.color}
                    caption={status.tooltip}
                    position="left"
                  >
                    <PieChart
                      progress={forecastToPrevious}
                      size={52}
                      strokeWidth={8}
                      ml={theme.spacing.spacing03}
                      color={status.color}
                    >
                      <SVGIcon name="forecastUp" color={status.color} />
                    </PieChart>
                  </Tooltip>
                </Hidden>
                <div>
                  <Text
                    size="lg"
                    weight="medium"
                    tag="div"
                    mb={theme.spacing.spacing01}
                    ml={theme.spacing.spacing04}
                  >
                    <Link to="/estate?page=0&unassigned=true">
                      Unallocated Costs
                    </Link>
                  </Text>
                  <FlexContainer>
                    <Text size="sm" tag="span" ml={theme.spacing.spacing04}>
                      {resources} estate records
                    </Text>
                  </FlexContainer>
                </div>
              </FlexContainer>
            </Col>
            <Col sm={3} md={4} lg={5}>
              <FlexContainer justifyContent="flex-end">
                <div
                  style={{
                    width: "13rem",
                    paddingLeft: theme.spacing.spacing08,
                    borderLeft: `1px solid ${theme.color.border.border01}`,
                  }}
                >
                  <FlexContainer justifyContent="flex-end" alignItems="end">
                    <div style={{ paddingRight: theme.spacing.spacing02 }}>
                      <Text size="sm" tag="div" mb={theme.spacing.spacing01}>
                        Monthly Costs
                      </Text>
                    </div>
                  </FlexContainer>
                  <FlexContainer justifyContent="flex-end" alignItems="end">
                    <div style={{ paddingRight: theme.spacing.spacing02 }}>
                      <Text weight="medium" tag="div">
                        {formatMoneyWithFallback(
                          application?.cost?.currentMonth,
                          currency
                        )}
                      </Text>
                    </div>
                  </FlexContainer>
                </div>
                <Hidden xs sm>
                  <div
                    style={{
                      width: "13rem",
                      paddingLeft: "4rem",
                      borderLeft: `1px solid ${theme.color.border.border01}`,
                    }}
                  >
                    <FlexContainer justifyContent="flex-end" alignItems="end">
                      <div style={{ paddingRight: theme.spacing.spacing02 }}>
                        <Text
                          size="sm"
                          tag="div"
                          width="7rem"
                          mb={theme.spacing.spacing01}
                          align="right"
                        >
                          Monthly Forecast
                        </Text>
                      </div>
                    </FlexContainer>
                    <FlexContainer justifyContent="flex-end" alignItems="end">
                      <When
                        condition={application?.cloudWasteAggregate?.hasWaste}
                      >
                        <When condition={showCo2eSavings}>
                          <Co2eSavings
                            iconOnly
                            iconSize="md"
                            co2eSavings={parseFloat(
                              application?.cloudWasteAggregate?.co2e ?? "0"
                            )}
                          />
                        </When>
                        <WasteIndicator
                          size="md"
                          reason="Application has Savings Suggestions"
                          amount={{
                            amount:
                              application?.cloudWasteAggregate?.cost ?? "0",
                            uri: `${ROUTES.estateRecords.index}?page=0&waste=true&unassigned=true`,
                          }}
                        />
                      </When>
                      <div
                        style={{
                          paddingRight: theme.spacing.spacing02,
                        }}
                      >
                        <Text
                          tag="div"
                          ml={theme.spacing.spacing02}
                          weight="medium"
                        >
                          {formatMoneyWithFallback(
                            application?.cost?.forecast,
                            currency
                          )}
                        </Text>
                      </div>
                    </FlexContainer>
                  </div>

                  <div
                    style={{
                      width: "13rem",
                      paddingLeft: theme.spacing.spacing08,
                      borderLeft: `1px solid ${theme.color.border.border01}`,
                    }}
                  >
                    <FlexContainer justifyContent="flex-end" alignItems="end">
                      <div style={{ paddingRight: theme.spacing.spacing02 }}>
                        <Text
                          size="sm"
                          tag="div"
                          width="7rem"
                          mb={theme.spacing.spacing01}
                          align="right"
                        >
                          Previous Month
                        </Text>
                      </div>
                    </FlexContainer>
                    <div>
                      <FlexContainer justifyContent="flex-end" alignItems="end">
                        <div style={{ paddingRight: theme.spacing.spacing02 }}>
                          <Text
                            tag="div"
                            ml={theme.spacing.spacing02}
                            weight="medium"
                          >
                            {formatMoneyWithFallback(
                              application?.cost?.previousMonth,
                              currency
                            )}
                          </Text>
                        </div>
                      </FlexContainer>
                    </div>
                  </div>
                </Hidden>
              </FlexContainer>
            </Col>
          </Row>
        </LoaderWrap>
      </Box>
    </Box>
  );
}
